import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { AppFilterType, FilterItem, filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { JobsTable } from "@/pages/recruit/jobs/components/JobsTable.tsx";
import { OffersTable } from "@/pages/recruit/jobs/components/OffersTable.tsx";
import { OfferEditModal } from "@/pages/recruit/offers/widgets/OfferEditModal.tsx";
import { OfferRequestViewModal } from "@/pages/recruit/offers/widgets/OfferRequestViewModal.tsx";
import { useJobDuplicate, useJobsSearch } from "@/queries/recruit/use-jobs.query.ts";
import {
  useOfferDelete,
  useOfferRequestCancel,
  useOffersSearch,
} from "@/queries/recruit/use-offer.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { getApprovalStatusSelectItems } from "@/types/approvals/approval.types.ts";
import { ApiJob, ApiJobsFilter, JobStatus, JobStatusItems } from "@/types/recruit/job.types.ts";
import { ApiOffersFilter } from "@/types/recruit/offer.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  ButtonLink,
  ConfirmationModal,
  CoreFilterType,
  FilterRenderType,
  FilterValueType,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  Stack,
  TabButton,
  TabList,
  useModals,
} from "@skailer-com/uikit";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

enum JobTabsSection {
  my = "my",
  opened = "opened",
  all = "all",
  offers = "offers",
}

export default function JobsIndex() {
  const { t } = useTranslation();
  const { canIEdit, canIView, isFeatureEnabled } = usePermissions();
  const modalsService = useModals();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo(() => {
    const tabParam = searchParams.get("tab");
    return (tabParam as JobTabsSection) || JobTabsSection.opened;
  }, [searchParams]);

  const canEditJob = canIEdit(ApiPermission.manage_jobs);
  const canViewOffers = canIView(ApiPermission.manage_offers);

  const [jobsSearchTerm, setJobsSearchTerm] = useState<string | undefined>(undefined);
  const [offersSearchTerm, setOffersSearchTerm] = useState<string | undefined>(undefined);

  useTitle(
    activeTab == JobTabsSection.offers ? t("ats:job_offers") : t("ats:jobs"),
    t("ats:recruit")
  );

  const [syncedSearchParams, setSyncedSearchParams] = useState<URLSearchParams>();
  if (searchParams !== syncedSearchParams) {
    setSyncedSearchParams(searchParams);
    const searchParam = searchParams.get("search");
    if (activeTab === JobTabsSection.offers) {
      setOffersSearchTerm(searchParam || undefined);
    } else {
      setJobsSearchTerm(searchParam || undefined);
    }
  }

  useDebounceUpdate({
    data: jobsSearchTerm,
    timeout: 300,
    execute: (newData) => {
      if (activeTab !== JobTabsSection.offers) {
        setSearchParams((searchParams) => {
          if (newData) searchParams.set("search", newData);
          else searchParams.delete("search");
          searchParams.set("page", "1");
          return searchParams;
        });
      }
    },
  });

  useDebounceUpdate({
    data: offersSearchTerm,
    timeout: 300,
    execute: (newData) => {
      if (activeTab === JobTabsSection.offers) {
        setSearchParams((searchParams) => {
          if (newData) searchParams.set("search", newData);
          else searchParams.delete("search");
          searchParams.set("page", "1");
          return searchParams;
        });
      }
    },
  });

  const jobDuplicate = useJobDuplicate();

  const handleJobDuplicate = (job: ApiJob) => {
    modalsService.openModal(ConfirmationModal, {
      question: t("ats:duplicate_job"),
      text: t("ats:duplicate_job_confirm"),
      acceptTitle: t("common:duplicate"),
      onAccept: async function () {
        try {
          await jobDuplicate.mutateAsync({ id: job.id });

          analyticsService.trackEvent(analyticEvents.jobs.duplicated, {
            [analyticProperties.id]: job.id,
          });
        } catch {
          return;
        }
      },
    });
  };

  const offerRequestCancel = useOfferRequestCancel();
  const offerRequestDelete = useOfferDelete();

  const handleOfferRequestCancel = async (id: number) => {
    modalsService.openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("approval:cancel_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          await offerRequestCancel.mutateAsync(id);

          analyticsService.trackEvent(analyticEvents.offers.requestAction, {
            [analyticProperties.id]: id,
            [analyticProperties.title]: "Job Offer",
            [analyticProperties.actionType]: "Cancel",
          });
        } catch {
          return;
        }
      },
    });
  };

  const handleOfferRequestDelete = async (id: number) => {
    modalsService.openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("core:delete_request_confirm_description"),
      acceptTitle: t("common:yes"),
      onAccept: async () => {
        try {
          await offerRequestDelete.mutateAsync(id);
          analyticsService.trackEvent(analyticEvents.offers.requestAction, {
            [analyticProperties.id]: id,
            [analyticProperties.title]: "Job Offer",
            [analyticProperties.actionType]: "Delete",
          });
        } catch {
          return;
        }
      },
    });
  };

  const library = filtersLibrary();

  const filterByActiveTab = useMemo(() => {
    const result: FilterItem[] = [library.page_number, library.text];

    switch (activeTab) {
      case JobTabsSection.my:
        result.push({
          name: "my_jobs",
          type: CoreFilterType.hidden,
          valueType: FilterValueType.boolean,
          renderType: FilterRenderType.none,
          defaultValue: true,
        });
        result.push({
          name: "status_list",
          type: CoreFilterType.urlParam,
          valueType: FilterValueType.stringArray,
          renderType: FilterRenderType.multiselect,
          label: t("common:status"),
          options: JobStatusItems(),
          defaultValue: [JobStatus.published, JobStatus.on_hold],
        });
        break;
      case JobTabsSection.opened:
        result.push({
          name: "status_list",
          type: CoreFilterType.urlParam,
          valueType: FilterValueType.stringArray,
          renderType: FilterRenderType.multiselect,
          label: t("common:status"),
          options: JobStatusItems(),
          defaultValue: [JobStatus.published, JobStatus.on_hold],
        });
        break;
      case JobTabsSection.all:
        result.push({
          name: "status_list",
          type: CoreFilterType.urlParam,
          valueType: FilterValueType.stringArray,
          renderType: FilterRenderType.multiselect,
          label: t("common:status"),
          options: JobStatusItems(),
          defaultValue: [],
        });
        break;
    }

    result.push(library.department_ids);
    result.push({
      ...library.employee_ids,
      name: "recruiter_ids",
      label: t("ats:recruiters"),
    });
    result.push({
      ...library.employee_ids,
      name: "stakeholder_ids",
      label: t("ats:stakeholders"),
    });
    result.push({
      name: "date_creation_from",
      type: AppFilterType.jobCreationDate,
      valueType: FilterValueType.date,
      renderType: FilterRenderType.custom,
    });
    result.push({
      name: "date_creation_to",
      type: CoreFilterType.hidden,
      valueType: FilterValueType.date,
      renderType: FilterRenderType.none,
    });
    return result;
  }, [activeTab]);

  const filterOffers = useMemo(() => {
    return [
      library.page_number,
      library.text,
      {
        name: "statuses",
        type: CoreFilterType.urlParam,
        valueType: FilterValueType.stringArray,
        renderType: FilterRenderType.multiselect,
        label: t("common:status"),
        options: getApprovalStatusSelectItems(),
      },
      {
        ...library.employee_ids,
        name: "manager_ids",
        label: t("core:managers"),
      },
      {
        ...library.employee_ids,
        name: "created_by_ids",
        label: t("common:created_by"),
      },
      library.location_ids,
    ] as FilterItem[];
  }, []);

  const { filter: jobsFilter, filterButton: jobsFilterButton } = useAppFilter<ApiJobsFilter>({
    filters: filterByActiveTab,
    source: "Jobs",
  });

  const { filter: offersFilter, filterButton: offersFilterButton } = useAppFilter<ApiOffersFilter>({
    filters: filterOffers,
    source: "Offers",
  });

  const {
    data: jobs,
    isLoading: isJobsLoading,
    isFetching: isJobsFetching,
  } = useJobsSearch(jobsFilter.filter, activeTab !== JobTabsSection.offers);

  const {
    data: offers,
    isLoading: isOffersLoading,
    isFetching: isOffersFetching,
  } = useOffersSearch(offersFilter.filter, activeTab === JobTabsSection.offers && canViewOffers);
  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.jobs.viewed);
  });

  const handleChangeTab = useCallback(
    (newTab: JobTabsSection) => {
      const switchingBetweenSections =
        (activeTab === JobTabsSection.offers) !== (newTab === JobTabsSection.offers);

      if (switchingBetweenSections) {
        const newParams = new URLSearchParams();
        newParams.set("tab", newTab);
        newParams.delete("page");
        setSearchParams(newParams);
      } else {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("tab", newTab);

        switch (newTab) {
          case JobTabsSection.my:
            newParams.set("my_jobs", "true");
            break;
          case JobTabsSection.all:
            newParams.delete("status_list");
            newParams.delete("my_jobs");
            break;
          default:
            newParams.delete("my_jobs");
            break;
        }

        setSearchParams(newParams);
      }
    },
    [activeTab, searchParams, setSearchParams]
  );

  return (
    <>
      <Page>
        <Page.Header title={t("ats:jobs")}>
          {activeTab !== JobTabsSection.offers ? (
            <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
              {isFeatureEnabled(ApiFeatureType.ats_scorecards) &&
                canIEdit(ApiPermission.ats_manage_scorecards) && (
                  <ButtonLink href={"/recruitment/scorecards"} variant={"secondary"}>
                    {t("scorecards:scorecards")}
                  </ButtonLink>
                )}
              {jobsFilterButton()}
              {canEditJob && (
                <ButtonLink
                  href={"/recruitment/jobs/new"}
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
                  onClick={() => {
                    sessionStorage.setItem(
                      "job_edit_referrer",
                      location.pathname + location.search
                    );
                  }}
                >
                  {t("common:create_new")}
                </ButtonLink>
              )}
            </Stack>
          ) : (
            <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
              {offersFilterButton()}
            </Stack>
          )}
        </Page.Header>
        <Page.Content>
          <TabList>
            <TabButton
              onClick={() => handleChangeTab(JobTabsSection.opened)}
              isActive={activeTab == JobTabsSection.opened}
              title={t("ats:active_my_jobs")}
            />
            <TabButton
              onClick={() => handleChangeTab(JobTabsSection.my)}
              isActive={activeTab == JobTabsSection.my}
              title={t("ats:my_jobs")}
            />

            <TabButton
              onClick={() => handleChangeTab(JobTabsSection.all)}
              isActive={activeTab == JobTabsSection.all}
              title={t("ats:all_jobs")}
            />
            {canViewOffers && (
              <TabButton
                onClick={() => handleChangeTab(JobTabsSection.offers)}
                isActive={activeTab == JobTabsSection.offers}
                title={t("ats:job_offers")}
              />
            )}
          </TabList>
          <Stack direction={"horizontal"}>
            <Search
              onInput={(e) => {
                if (activeTab === JobTabsSection.offers) {
                  setOffersSearchTerm(e.currentTarget.value);
                } else {
                  setJobsSearchTerm(e.currentTarget.value);
                }
              }}
              type={"text"}
              value={activeTab === JobTabsSection.offers ? offersSearchTerm : jobsSearchTerm}
              placeholder={t("common:search")}
              className={"w-96"}
            />
          </Stack>
          {activeTab === JobTabsSection.offers ? (
            <>
              <OffersTable
                data={offers?.items}
                isLoading={isOffersLoading}
                isFetching={isOffersFetching}
                showNotFoundError={
                  offersFilter.filter.text != "" && offers && offers.metadata.total_items == 0
                }
                notFoundComponent={<EmptyStateNothingFound />}
                emptyStateComponent={<EmptyState />}
                onView={(request) => {
                  if (request.approval_request_details?.id) {
                    modalsService.openModal(OfferRequestViewModal, {
                      requestId: request.approval_request_details.id,
                    });
                  }
                }}
                onEdit={(request) => {
                  modalsService.openModal(OfferEditModal, {
                    offerRequest: request,
                    candidateId: request.offer_data.candidate.id,
                  });
                }}
                onCancel={(id) => {
                  handleOfferRequestCancel(id);
                }}
                onDelete={(id) => {
                  handleOfferRequestDelete(id);
                }}
              />

              {offers && (
                <PaginationBlock
                  metadata={offers.metadata}
                  onPageChange={(page) =>
                    setSearchParams((prev) => {
                      const newParams = new URLSearchParams(prev);
                      newParams.set(library.page_number.searchParamName, page.toString());
                      return newParams;
                    })
                  }
                />
              )}
            </>
          ) : (
            <>
              <JobsTable
                data={jobs?.items}
                isLoading={isJobsLoading}
                isFetching={isJobsFetching}
                onEdit={
                  canEditJob
                    ? (job: ApiJob) => {
                        sessionStorage.setItem(
                          "job_edit_referrer",
                          location.pathname + location.search
                        );
                        navigate(`/recruitment/jobs/${job.id}/edit/`);
                      }
                    : undefined
                }
                onDuplicate={canEditJob ? handleJobDuplicate : undefined}
                showNotFoundError={
                  jobsFilter.filter.text != "" && jobs && jobs.metadata.total_items == 0
                }
                notFoundComponent={<EmptyStateNothingFound />}
                emptyStateComponent={<EmptyState />}
              />
              {jobs && (
                <PaginationBlock
                  metadata={jobs?.metadata}
                  onPageChange={(page) => {
                    setSearchParams((searchParams) => {
                      searchParams.set("page", page.toString());
                      return searchParams;
                    });
                  }}
                />
              )}
            </>
          )}
        </Page.Content>
      </Page>
    </>
  );
}
